/**
 * One voice call, from this browser's side: the microphone, a WebRTC
 * connection to every other peer in the room's voice chat, and who is
 * speaking.
 *
 * The topology is a mesh — each peer connects to each other peer — which
 * needs no media server and is fine for the handful of people a chat room
 * holds (the server caps it). Negotiation is fixed so it can never collide:
 * whoever joins offers to everyone already there, and those only answer.
 * Signals travel over the chat socket as `voiceSignal` frames; audio goes
 * directly between browsers or, behind NAT that forbids that, through the
 * TURN relay the server names in `voiceJoined`.
 *
 * The class knows nothing of React or the socket. The store feeds it frames
 * and listens for `change` to re-render.
 */

import type { IceServer, VoiceSignal } from "@/chat/protocol"

export type VoiceEvents = {
  /** A signal for the server to relay to `to`. */
  signal: (to: string, signal: VoiceSignal) => void
  /** Something the UI shows changed: who is speaking, a connection state. */
  change: () => void
}

/** Stand-in key for this browser's own microphone in `speaking`. */
export const SELF = "self"

/** RMS level above which a stream counts as someone talking. */
const SPEAKING_LEVEL = 0.02
const METER_INTERVAL_MS = 150

export class MicrophoneError extends Error {
  constructor(cause: unknown) {
    const name = cause instanceof DOMException ? cause.name : ""
    super(
      name === "NotAllowedError" || name === "SecurityError"
        ? "Нет доступа к микрофону. Разрешите его в настройках браузера."
        : name === "NotFoundError"
          ? "Микрофон не найден."
          : "Не удалось включить микрофон."
    )
    this.name = "MicrophoneError"
  }
}

type Meter = { analyser: AnalyserNode; source: MediaStreamAudioSourceNode }

type Peer = {
  pc: RTCPeerConnection
  audio: HTMLAudioElement
  offerer: boolean
  /** Candidates that arrived before the remote description they belong to. */
  pendingIce: RTCIceCandidateInit[]
  /** Signals from one peer are applied strictly in order. */
  chain: Promise<void>
}

export class VoiceCall {
  private stream: MediaStream | null = null
  private context: AudioContext | null = null
  private readonly peers = new Map<string, Peer>()
  private readonly meters = new Map<string, Meter>()
  private iceServers: RTCIceServer[] = []
  private meterTimer: ReturnType<typeof setInterval> | null = null
  private closed = false

  /** Peer ids (and `SELF`) whose audio is above the speaking level. */
  speaking = new Set<string>()
  muted = false

  private readonly events: VoiceEvents

  constructor(events: VoiceEvents) {
    this.events = events
  }

  /**
   * Opens the microphone. Must run from a user gesture: that is what lets
   * the audio context and the remote `<audio>` elements start on iOS.
   * Rejects with a `MicrophoneError`.
   */
  async start(): Promise<void> {
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: false,
      })
    } catch (error) {
      throw new MicrophoneError(error)
    }

    // A call that was hung up while the permission prompt was open.
    if (this.closed) {
      this.stopMicrophone()
      return
    }

    try {
      this.context = new AudioContext()
      void this.context.resume()
      this.meter(SELF, this.stream)
    } catch {
      // No Web Audio: calls still work, only without the speaking ring.
    }

    this.meterTimer = setInterval(() => this.measure(), METER_INTERVAL_MS)
  }

  /** Whether the microphone is open, so connections have audio to carry. */
  get ready(): boolean {
    return this.stream !== null && !this.closed
  }

  /** The relay to use for every connection made from now on. */
  configure(iceServers: IceServer[]): void {
    this.iceServers = iceServers
  }

  /** Offers to each of `peerIds`: the people already in the chat on join. */
  connectTo(peerIds: string[]): void {
    for (const peerId of peerIds) {
      if (this.peers.has(peerId)) continue
      const peer = this.createPeer(peerId, true)
      this.enqueue(peer, () => this.offer(peerId, peer))
    }
  }

  handleSignal(from: string, signal: VoiceSignal): void {
    if (this.closed) return

    // Only an offer may open a connection; anything else for a peer we do
    // not know belongs to one that is already gone.
    let peer = this.peers.get(from)
    if (!peer) {
      if (signal.kind !== "offer") return
      peer = this.createPeer(from, false)
    }

    const target = peer
    this.enqueue(target, async () => {
      const { pc } = target

      if (signal.kind === "ice") {
        if (pc.remoteDescription) await addCandidate(pc, signal.candidate)
        else target.pendingIce.push(signal.candidate)
        return
      }

      await pc.setRemoteDescription({ type: signal.kind, sdp: signal.sdp })
      for (const candidate of target.pendingIce.splice(0)) {
        await addCandidate(pc, candidate)
      }

      if (signal.kind === "offer") {
        await pc.setLocalDescription(await pc.createAnswer())
        this.events.signal(from, {
          kind: "answer",
          sdp: pc.localDescription!.sdp,
        })
      }
    })
  }

  /** Drops connections to peers no longer in the chat. */
  retain(peerIds: Set<string>): void {
    for (const peerId of [...this.peers.keys()]) {
      if (!peerIds.has(peerId)) this.dropPeer(peerId)
    }
  }

  /** Closes every connection, keeping the microphone, for a rejoin. */
  resetPeers(): void {
    for (const peerId of [...this.peers.keys()]) this.dropPeer(peerId)
  }

  setMuted(muted: boolean): void {
    this.muted = muted
    for (const track of this.stream?.getAudioTracks() ?? []) {
      track.enabled = !muted
    }
    this.measure()
  }

  /** How each connection is doing, for the "connecting…" hint. */
  connectionStates(): Record<string, RTCPeerConnectionState> {
    return Object.fromEntries(
      [...this.peers].map(([peerId, peer]) => [peerId, peer.pc.connectionState])
    )
  }

  close(): void {
    if (this.closed) return
    this.closed = true

    this.resetPeers()
    this.stopMicrophone()
    if (this.meterTimer) clearInterval(this.meterTimer)
    for (const meter of this.meters.values()) meter.source.disconnect()
    this.meters.clear()
    void this.context?.close().catch(() => {})
    this.speaking.clear()
  }

  // --- Connections ---------------------------------------------------------

  private createPeer(peerId: string, offerer: boolean): Peer {
    const pc = new RTCPeerConnection({ iceServers: this.iceServers })

    for (const track of this.stream?.getAudioTracks() ?? []) {
      pc.addTrack(track, this.stream!)
    }

    const audio = new Audio()
    audio.autoplay = true
    audio.setAttribute("playsinline", "")

    const peer: Peer = {
      pc,
      audio,
      offerer,
      pendingIce: [],
      chain: Promise.resolve(),
    }
    this.peers.set(peerId, peer)

    pc.onicecandidate = (event) => {
      if (!event.candidate) return
      const { candidate, sdpMid, sdpMLineIndex } = event.candidate
      this.events.signal(peerId, {
        kind: "ice",
        candidate: {
          candidate,
          sdpMid: sdpMid ?? null,
          sdpMLineIndex: sdpMLineIndex ?? null,
        },
      })
    }

    pc.ontrack = (event) => {
      const stream = event.streams[0] ?? new MediaStream([event.track])
      audio.srcObject = stream
      // Autoplay is allowed here: the page already captures the microphone.
      void audio.play().catch(() => {})
      this.meter(peerId, stream)
    }

    pc.onconnectionstatechange = () => {
      // A network change (Wi-Fi to mobile, say) kills the chosen route. The
      // side that offered asks for new candidates; the other just answers.
      if (pc.connectionState === "failed" && peer.offerer) {
        this.enqueue(peer, () => this.offer(peerId, peer, true))
      }
      this.events.change()
    }

    return peer
  }

  private async offer(peerId: string, peer: Peer, iceRestart = false) {
    const offer = await peer.pc.createOffer({ iceRestart })
    await peer.pc.setLocalDescription(offer)
    this.events.signal(peerId, {
      kind: "offer",
      sdp: peer.pc.localDescription!.sdp,
    })
  }

  private enqueue(peer: Peer, step: () => Promise<void>): void {
    peer.chain = peer.chain.then(step).catch((error: unknown) => {
      // One bad signal must not wedge the queue for the rest of the call.
      console.warn("voice negotiation failed:", error)
    })
  }

  private dropPeer(peerId: string): void {
    const peer = this.peers.get(peerId)
    if (!peer) return

    this.peers.delete(peerId)
    peer.pc.onconnectionstatechange = null
    peer.pc.close()
    peer.audio.srcObject = null
    this.meters.get(peerId)?.source.disconnect()
    this.meters.delete(peerId)
    this.speaking.delete(peerId)
    this.events.change()
  }

  private stopMicrophone(): void {
    for (const track of this.stream?.getTracks() ?? []) track.stop()
    this.stream = null
  }

  // --- Speaking ------------------------------------------------------------

  private meter(key: string, stream: MediaStream): void {
    if (!this.context) return
    try {
      this.meters.get(key)?.source.disconnect()
      const source = this.context.createMediaStreamSource(stream)
      const analyser = this.context.createAnalyser()
      analyser.fftSize = 512
      source.connect(analyser)
      this.meters.set(key, { analyser, source })
    } catch {
      // A stream without audio yet; the next track will try again.
    }
  }

  private measure(): void {
    const next = new Set<string>()
    const samples = new Float32Array(512)

    for (const [key, { analyser }] of this.meters) {
      if (key === SELF && this.muted) continue
      analyser.getFloatTimeDomainData(samples)
      let sum = 0
      for (const sample of samples) sum += sample * sample
      if (Math.sqrt(sum / samples.length) > SPEAKING_LEVEL) next.add(key)
    }

    const changed =
      next.size !== this.speaking.size ||
      [...next].some((key) => !this.speaking.has(key))
    this.speaking = next
    if (changed) this.events.change()
  }
}

async function addCandidate(
  pc: RTCPeerConnection,
  candidate: RTCIceCandidateInit
): Promise<void> {
  try {
    await pc.addIceCandidate(candidate)
  } catch {
    // A candidate for a transport that is already gone; harmless.
  }
}

export function voiceSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "RTCPeerConnection" in window &&
    !!navigator.mediaDevices?.getUserMedia
  )
}
